import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiPlay, FiMessageCircle, FiMapPin, FiStar } = FiIcons;

const Hero = () => {
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi! I'm interested in booking Villa Springwater. Can you check availability?");
    window.open(`https://wa.me/94704003478?text=${message}`, '_blank');
  };

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
      {/* Animated Background Blobs */}
      <div className="liquid-blob"></div>
      <div className="liquid-blob"></div>
      <div className="liquid-blob"></div>
      
      {/* Hero Background Image/Video */}
      <div className="absolute inset-0 z-0">
        <div className="relative w-full h-full">
          {!isVideoPlaying ? (
            <motion.div
              className="w-full h-full bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: `linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url('https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')`
              }}
              initial={{ scale: 1.1 }}
              animate={{ scale: 1 }}
              transition={{ duration: 2, ease: "easeOut" }}
            >
              <motion.button
                className="absolute inset-0 flex items-center justify-center group"
                onClick={() => setIsVideoPlaying(true)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="glass-morphism rounded-full p-6 group-hover:bg-white/20 transition-all duration-300">
                  <SafeIcon icon={FiPlay} className="text-white text-4xl" />
                </div>
              </motion.button>
            </motion.div>
          ) : (
            <video
              className="w-full h-full object-cover"
              autoPlay
              muted
              loop
              playsInline
            >
              <source src="https://cdn.coverr.co/videos/coverr-aerial-view-of-a-tropical-river-4448/1080p.mp4" type="video/mp4" />
            </video>
          )}
        </div>
      </div>

      {/* Hero Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <motion.div
          className="glass-morphism rounded-3xl p-8 md:p-12 max-w-4xl mx-auto"
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          {/* Rating Badge */}
          <motion.div
            className="inline-flex items-center gap-2 bg-emerald-500/20 text-emerald-300 px-4 py-2 rounded-full mb-6"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.5 }}
          >
            <SafeIcon icon={FiStar} className="text-yellow-400" />
            <span className="text-sm font-medium">9.4/10 Guest Rating</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1
            className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
          >
            Nature's Sanctuary
            <br />
            <span className="gradient-primary bg-clip-text text-transparent">
              in Dellawa
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            className="text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
          >
            The Real Sinharaja Experience
            <br />
            <span className="text-emerald-400 font-semibold">
              River bathing, natural spring pool, and raw jungle luxury
            </span>
          </motion.p>

          {/* Price Badge */}
          <motion.div
            className="inline-block glass-dark rounded-2xl px-6 py-3 mb-8"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1 }}
          >
            <span className="text-gray-400 text-lg">Starting at just</span>
            <span className="text-3xl md:text-4xl font-bold text-white ml-2">$17</span>
            <span className="text-emerald-400 text-lg">/night</span>
          </motion.div>

          {/* CTA Buttons */}
          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center items-center"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.2 }}
          >
            <motion.button
              onClick={handleWhatsAppClick}
              className="gradient-primary text-white px-8 py-4 rounded-2xl font-semibold text-lg flex items-center gap-3 shadow-xl hover:shadow-2xl transition-all duration-300 min-w-[250px]"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
            >
              <SafeIcon icon={FiMessageCircle} className="text-xl" />
              Check Availability (WhatsApp)
            </motion.button>

            <motion.div
              className="flex items-center gap-2 text-gray-300"
              whileHover={{ scale: 1.05 }}
            >
              <SafeIcon icon={FiMapPin} className="text-emerald-400" />
              <span className="text-sm">Dellawa, Sinharaja Buffer Zone</span>
            </motion.div>
          </motion.div>
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2 }}
      >
        <motion.div
          className="w-6 h-10 border-2 border-white/50 rounded-full flex justify-center"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div
            className="w-1 h-3 bg-white/70 rounded-full mt-2"
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
          />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Hero;